<template>
  <div
    class="layout-element-wrapper"
    :class="{ selected: selected }"
    @click="onElementClick"
  >
    <!-- Group Element -->
    <div v-if="element.type === 'group'" class="group-element">
      <v-card
        variant="outlined"
        class="group-card"
        :class="{ 'drag-over': isDragOverGroup }"
        @drop="onGroupDrop"
        @dragover.prevent
        @dragenter="onGroupDragEnter"
        @dragleave="onGroupDragLeave"
      >
        <v-card-title v-if="element.label" class="text-subtitle-2 py-2">
          {{ element.label }}
        </v-card-title>
        <v-divider v-if="element.label"></v-divider>
        <v-card-text class="pa-5">
          <div class="group-children container-fluid">
            <div class="row">
              <div
                v-for="child in (element as GroupElement).children"
                :key="child.id"
                :class="child.class"
              >
                <layout-element-component
                  :element="child"
                  :selected="props.selectedElementId === child.id"
                  :selected-element-id="props.selectedElementId"
                  @select="$emit('select', $event)"
                  @delete="$emit('delete', $event)"
                  @update="$emit('update', $event)"
                  @group-drop="$emit('groupDrop', $event)"
                  @label-drop="$emit('labelDrop', $event)"
                />
              </div>
              <div
                v-if="(element as GroupElement).children.length === 0"
                class="empty-group col-12"
              >
                Empty Group - Drop elements here
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Input Element -->
    <div v-else-if="element.type === 'input'" class="input-element">
      <v-text-field
        :label="element.label || 'Input'"
        class="pa-2"
        variant="outlined"
        density="compact"
        readonly
        :model-value="getPreviewValue(element as InputElement)"
      />
    </div>

    <!-- Big Label Element -->
    <div
      v-else-if="element.type === 'bigTextSmallLabel'"
      class="big-label-element"
    >
      <div class="big-label-content d-flex flex-column align-center">
        <!-- Child Label (if present) -->
        <div
          v-if="(element as BigLabelElement).children"
          class="child-label-container d-flex align-center gap-1 mb-1"
          :class="{ 'selected-child': props.selectedElementId === (element as BigLabelElement).children!.id }"
          @click.stop="onChildLabelClick"
        >
          <span class="text-caption text-medium-emphasis">
            {{ (element as BigLabelElement).children!.label }}
          </span>
          <v-btn
            size="x-small"
            icon
            variant="text"
            color="grey"
            @click.stop="removeChildLabel"
          >
            <v-icon size="12">mdi-close</v-icon>
          </v-btn>
        </div>
        <!-- Main Big Label -->
        <div class="text-h3 font-weight-bold">
          {{ element.label || 'Big Label' }}
        </div>

        <!-- Drop Zone for Labels (only show when no child exists) -->
        <div
          v-if="!(element as BigLabelElement).children"
          class="label-drop-zone"
          :class="{ 'drag-over': isDragOverLabel }"
          @drop="onLabelDrop"
          @dragover.prevent
          @dragenter.prevent="isDragOverLabel = true"
          @dragleave.prevent="isDragOverLabel = false"
        >
          <div class="drop-zone-content">
            <v-icon size="small" color="grey">mdi-plus</v-icon>
            <span class="text-caption text-grey">Drop label here</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Row Break Element -->
    <div v-else-if="element.type === 'row-break'" class="row-break-element">
      <v-divider class="my-2"></v-divider>
      <div class="text-center text-caption">Row Break</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  LayoutElement,
  GroupElement,
  InputElement,
  BigLabelElement,
} from '@/Core.Service.Domain/Controls/AHUs/types/AHULayout';
import { ref } from 'vue';

// Recursive import
import LayoutElementComponent from './LayoutElement.vue';

interface Props {
  element: LayoutElement;
  selected: boolean;
  selectedElementId?: string; // Add selectedElementId prop to track deeply nested selection
}

const props = defineProps<Props>();

// Drag state
const isDragOverLabel = ref(false);
const isDragOverGroup = ref(false);

const emit = defineEmits<{
  select: [element: LayoutElement];
  delete: [elementId: string];
  update: [element: LayoutElement];
  groupDrop: [payload: { parentId: string; dropEvent: DragEvent }];
  labelDrop: [payload: { parentId: string; dropEvent: DragEvent }];
}>();

// Handle element click with proper event propagation
const onElementClick = (event: MouseEvent) => {
  event.stopPropagation(); // Prevent event from bubbling up to parent elements
  emit('select', props.element);
};

// Handle child label click
const onChildLabelClick = (event: MouseEvent) => {
  event.stopPropagation(); // Prevent event from bubbling up to parent elements
  if (props.element.type === 'bigTextSmallLabel') {
    const bigLabel = props.element as BigLabelElement;
    if (bigLabel.children) {
      emit('select', bigLabel.children);
    }
  }
};

// Handle drop on group
const onGroupDrop = (event: DragEvent) => {
  event.stopPropagation(); // Prevent event from bubbling up
  isDragOverGroup.value = false; // Reset drag state
  emit('groupDrop', {
    parentId: props.element.id,
    dropEvent: event,
  });
};

// Handle drag enter on group
const onGroupDragEnter = (event: DragEvent) => {
  event.preventDefault();
  event.stopPropagation();
  isDragOverGroup.value = true;
};

// Handle drag leave on group
const onGroupDragLeave = (event: DragEvent) => {
  event.preventDefault();
  event.stopPropagation();
  // Only reset if we're actually leaving the group element
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const isOutside =
    event.clientX < rect.left ||
    event.clientX > rect.right ||
    event.clientY < rect.top ||
    event.clientY > rect.bottom;
  if (isOutside) {
    isDragOverGroup.value = false;
  }
};

// Handle drop on big label (for adding child labels)
const onLabelDrop = (event: DragEvent) => {
  event.stopPropagation(); // Prevent event from bubbling up
  isDragOverLabel.value = false; // Reset drag state
  emit('labelDrop', {
    parentId: props.element.id,
    dropEvent: event,
  });
};

// Remove child label from big label
const removeChildLabel = () => {
  if (props.element.type === 'bigTextSmallLabel') {
    const updatedElement = { ...props.element };
    (updatedElement as BigLabelElement).children = undefined;
    emit('update', updatedElement);
  }
};

// Preview value generation for inputs
const getPreviewValue = (element: InputElement): string => {
  switch (element.inputType) {
    case 'text':
      return 'Sample Text';
    case 'number':
      return '123.45';
    case 'date':
      return '2025-01-01';
    case 'big-centered-label':
      return '75.2°F';
    case 'red-green-circle':
      return 'Status';
    default:
      return 'Preview';
  }
};
</script>

<style lang="scss" scoped>
.layout-element-wrapper {
  position: relative;
  transition: all 0.2s ease;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 100%; // Ensure it fills the column height

  &.selected {
    outline: 2px solid #1976d2;
    outline-offset: 2px;
  }

  &:hover {
    background-color: rgba(25, 118, 210, 0.04);
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
}

// Bootstrap Grid System CSS for groups
.group-children {
  .container-fluid {
    width: 100%;
    padding-right: 15px;
    padding-left: 15px;
    margin-right: auto;
    margin-left: auto;
  }

  .row {
    display: flex;
    flex-wrap: wrap;
    margin-right: -15px;
    margin-left: -15px;
  }

  // Bootstrap Column Classes
  [class*='col-'] {
    position: relative;
    width: 100%;
    padding-right: 8px;
    padding-left: 8px;
    margin-bottom: 8px; // Add some vertical spacing between elements
  }

  // Large screen columns (lg)
  @media (min-width: 992px) {
    .col-lg-1 {
      flex: 0 0 8.333333%;
      max-width: 8.333333%;
    }
    .col-lg-2 {
      flex: 0 0 16.666667%;
      max-width: 16.666667%;
    }
    .col-lg-3 {
      flex: 0 0 25%;
      max-width: 25%;
    }
    .col-lg-4 {
      flex: 0 0 33.333333%;
      max-width: 33.333333%;
    }
    .col-lg-5 {
      flex: 0 0 41.666667%;
      max-width: 41.666667%;
    }
    .col-lg-6 {
      flex: 0 0 50%;
      max-width: 50%;
    }
    .col-lg-7 {
      flex: 0 0 58.333333%;
      max-width: 58.333333%;
    }
    .col-lg-8 {
      flex: 0 0 66.666667%;
      max-width: 66.666667%;
    }
    .col-lg-9 {
      flex: 0 0 75%;
      max-width: 75%;
    }
    .col-lg-10 {
      flex: 0 0 83.333333%;
      max-width: 83.333333%;
    }
    .col-lg-11 {
      flex: 0 0 91.666667%;
      max-width: 91.666667%;
    }
    .col-lg-12 {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }

  // Small screen columns (sm)
  @media (min-width: 576px) {
    .col-sm-1 {
      flex: 0 0 8.333333%;
      max-width: 8.333333%;
    }
    .col-sm-2 {
      flex: 0 0 16.666667%;
      max-width: 16.666667%;
    }
    .col-sm-3 {
      flex: 0 0 25%;
      max-width: 25%;
    }
    .col-sm-4 {
      flex: 0 0 33.333333%;
      max-width: 33.333333%;
    }
    .col-sm-5 {
      flex: 0 0 41.666667%;
      max-width: 41.666667%;
    }
    .col-sm-6 {
      flex: 0 0 50%;
      max-width: 50%;
    }
    .col-sm-7 {
      flex: 0 0 58.333333%;
      max-width: 58.333333%;
    }
    .col-sm-8 {
      flex: 0 0 66.666667%;
      max-width: 66.666667%;
    }
    .col-sm-9 {
      flex: 0 0 75%;
      max-width: 75%;
    }
    .col-sm-10 {
      flex: 0 0 83.333333%;
      max-width: 83.333333%;
    }
    .col-sm-11 {
      flex: 0 0 91.666667%;
      max-width: 91.666667%;
    }
    .col-sm-12 {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }

  // Default mobile-first (below sm breakpoint)
  .col-1 {
    flex: 0 0 8.333333%;
    max-width: 8.333333%;
  }
  .col-2 {
    flex: 0 0 16.666667%;
    max-width: 16.666667%;
  }
  .col-3 {
    flex: 0 0 25%;
    max-width: 25%;
  }
  .col-4 {
    flex: 0 0 33.333333%;
    max-width: 33.333333%;
  }
  .col-5 {
    flex: 0 0 41.666667%;
    max-width: 41.666667%;
  }
  .col-6 {
    flex: 0 0 50%;
    max-width: 50%;
  }
  .col-7 {
    flex: 0 0 58.333333%;
    max-width: 58.333333%;
  }
  .col-8 {
    flex: 0 0 66.666667%;
    max-width: 66.666667%;
  }
  .col-9 {
    flex: 0 0 75%;
    max-width: 75%;
  }
  .col-10 {
    flex: 0 0 83.333333%;
    max-width: 83.333333%;
  }
  .col-11 {
    flex: 0 0 91.666667%;
    max-width: 91.666667%;
  }
  .col-12 {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

.group-element {
  .group-card {
    min-height: 80px;
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    // Visual feedback during dragover
    &.drag-over {
      border: 2px solid #1976d2;
      box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
      background-color: rgba(25, 118, 210, 0.05);
    }
  }

  .group-children {
    min-height: 40px;
    position: relative;
  }

  .empty-group {
    text-align: center;
    color: #999;
    padding: 20px;
    border: 2px dashed #ddd;
    border-radius: 4px;
    transition: all 0.2s ease;

    &:hover {
      border-color: #1976d2;
      color: #1976d2;
      background-color: rgba(25, 118, 210, 0.05);
    }
  }
}

.big-label-element {
  padding: 16px;
  text-align: center;
  border: 1px solid #ddd;
  border-radius: 4px;

  .big-label-content {
    font-weight: bold;
    color: #1976d2;

    .child-label-container {
      background-color: rgba(25, 118, 210, 0.1);
      padding: 4px 8px;
      border-radius: 12px;
      border: 1px solid rgba(25, 118, 210, 0.2);
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(25, 118, 210, 0.15);
        border-color: rgba(25, 118, 210, 0.3);
      }

      &.selected-child {
        background-color: rgba(25, 118, 210, 0.2);
        border-color: #1976d2;
        box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.3);
      }
    }
  }

  .label-drop-zone {
    margin-top: 8px;
    padding: 8px;
    border: 2px dashed #ccc;
    border-radius: 4px;
    background-color: rgba(25, 118, 210, 0.02);
    transition: all 0.2s ease;
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      border-color: #1976d2;
      background-color: rgba(25, 118, 210, 0.05);
    }

    &.drag-over {
      border-color: #1976d2;
      background-color: rgba(25, 118, 210, 0.1);
      box-shadow: 0 2px 8px rgba(25, 118, 210, 0.2);
      transform: scale(1.02);
    }

    .drop-zone-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
    }
  }
}

.label-element {
  padding: 8px;
  text-align: center;

  .label-content {
    font-weight: 500;
    color: #555;
  }
}

.row-break-element {
  padding: 8px;
  text-align: center;
}
</style>
